import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { PRIVACY_LIMITS, privacyNoticeTemplate, retentionPeriodText, type PrivacySettings as Settings, type RetentionRow } from '@mahallu/shared';
import { ApiError, loadErrorKey, privacy, useMe, usePrivacySettings } from '../api';
import { ConfirmButton, FormSection, TextAreaField, TextField } from '../components/Form';
import { Icon } from '../components/Icon';
import { Badge, PageHeader } from '../components/Parts';
import { dateTime } from '../format';

const errorText = (e: unknown) => (e instanceof ApiError ? e.message : String(e));

/** Every privacy change answers with the whole settings object; put it straight into the cache. */
function useSettingsChange() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (fn: () => Promise<Settings>) => fn(),
    onSuccess: (s) => qc.setQueryData(['privacy', 'settings'], s),
  });
}

function Status({ error, saved }: { error: unknown; saved: boolean }) {
  const { t } = useTranslation();
  if (error) return <p className="form-error" role="alert">{errorText(error)}</p>;
  return saved ? <p className="note" role="status">{t('seq.saved')}</p> : null;
}

function OfficerCard({ s }: { s: Settings }) {
  const { t } = useTranslation();
  const change = useSettingsChange();
  const [name, setName] = useState(s.officer?.name ?? '');
  const [phone, setPhone] = useState(s.officer?.phone ?? '');
  const [email, setEmail] = useState(s.officer?.email ?? '');
  return (
    <section className="card">
      <div className="section-h">
        <h2>{t('privacy.officer')}</h2>
        {s.officer ? <Badge tone="ok">{t('privacy.named')}</Badge> : <Badge tone="warn">{t('privacy.notNamed')}</Badge>}
      </div>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          change.mutate(() => privacy.setOfficer({ name: name.trim(), phone: phone.trim() || null, email: email.trim() || null, version: s.officer?.version }));
        }}
      >
        <p className="note">{t('privacy.officerHint')}</p>
        <FormSection title={t('privacy.contact')}>
          <TextField label={t('privacy.officerName')} value={name} onChange={setName} maxLength={PRIVACY_LIMITS.officerName} required full />
          <TextField label={t('privacy.officerPhone')} value={phone} onChange={setPhone} maxLength={30} inputMode="tel" hint={t('form.phoneHint')} />
          <TextField label={t('privacy.officerEmail')} value={email} onChange={setEmail} maxLength={254} />
        </FormSection>
        <div className="action-row">
          <button type="submit" className="btn primary sm" disabled={change.isPending}>
            {t('privacy.saveOfficer')}
          </button>
          <Status error={change.error} saved={change.isSuccess} />
        </div>
      </form>
    </section>
  );
}

function NoticeCard({ s }: { s: Settings }) {
  const { t, i18n } = useTranslation();
  const change = useSettingsChange();
  const draft = s.draft;
  const [bodyEn, setBodyEn] = useState(draft?.bodyEn ?? '');
  const [bodyMl, setBodyMl] = useState(draft?.bodyMl ?? '');
  const [editing, setEditing] = useState(Boolean(draft) || !s.published);
  // A save or publish replaces the draft; show what the server now holds, and the published text once the draft is gone.
  useEffect(() => {
    setBodyEn(s.draft?.bodyEn ?? '');
    setBodyMl(s.draft?.bodyMl ?? '');
    if (!s.draft && s.published) setEditing(false);
  }, [s.draft?.version, s.draft?.versionNo, s.published?.versionNo]);

  const fillTemplate = () => {
    const input = { mahalluEn: s.mahallu.nameEn, mahalluMl: s.mahallu.nameMl, officer: s.officer, retention: s.retention };
    setBodyEn(privacyNoticeTemplate('en', input));
    setBodyMl(privacyNoticeTemplate('ml', input));
  };
  const dirty = bodyEn !== (draft?.bodyEn ?? '') || bodyMl !== (draft?.bodyMl ?? '');
  const lang = i18n.language === 'ml' ? 'ml' : 'en';

  return (
    <section className="card">
      <div className="section-h">
        <h2>{t('privacy.notice')}</h2>
        {s.published ? (
          <Badge tone="ok">{t('privacy.published', { n: s.published.versionNo, date: dateTime(s.published.publishedAt!, i18n.language) })}</Badge>
        ) : (
          <Badge tone="warn">{t('privacy.notPublishedShort')}</Badge>
        )}
        <span className="spacer" />
        {s.published ? (
          <a className="btn sm" href={s.publicPath} target="_blank" rel="noopener">
            <Icon name="eye" />
            {t('privacy.publicLink')}
          </a>
        ) : null}
      </div>
      {!s.published ? <p className="callout small">{t('privacy.notPublished')}</p> : null}
      {s.published && !editing ? (
        <>
          <p className={`notice-text${lang === 'ml' ? ' ml' : ''}`} lang={lang}>
            {lang === 'ml' ? s.published.bodyMl : s.published.bodyEn}
          </p>
          <div className="action-row">
            <button type="button" className="btn sm" onClick={() => setEditing(true)}>
              <Icon name="edit" />
              {t('privacy.newDraft')}
            </button>
          </div>
        </>
      ) : (
        <>
          <div className="section-h">
            <b>{draft ? t('privacy.draft', { n: draft.versionNo }) : t('privacy.newVersion')}</b>
            <span className="spacer" />
            <button type="button" className="btn sm" onClick={fillTemplate}>
              {t('privacy.template')}
            </button>
          </div>
          <p className="note">{t('privacy.templateHint')}</p>
          <div className="fgrid">
            <TextAreaField label={t('privacy.bodyEn')} value={bodyEn} onChange={setBodyEn} maxLength={PRIVACY_LIMITS.notice} rows={14} full />
            <TextAreaField label={t('privacy.bodyMl')} value={bodyMl} onChange={setBodyMl} maxLength={PRIVACY_LIMITS.notice} rows={14} lang="ml" full />
          </div>
          <div className="action-row">
            <button
              type="button"
              className="btn primary sm"
              disabled={change.isPending || !dirty}
              onClick={() => change.mutate(() => privacy.saveDraft({ bodyEn, bodyMl, version: draft?.version }))}
            >
              {t('privacy.saveDraft')}
            </button>
            {draft && !dirty ? <ConfirmButton label={t('privacy.publish')} disabled={change.isPending} onConfirm={() => change.mutate(() => privacy.publish(draft.version))} /> : null}
            {s.published && !draft ? (
              <button type="button" className="btn sm" onClick={() => setEditing(false)}>
                {t('form.cancel')}
              </button>
            ) : null}
            <Status error={change.error} saved={change.isSuccess} />
          </div>
          <p className="note">{t('privacy.publishHint')}</p>
        </>
      )}
    </section>
  );
}

function RetentionMonths({ r }: { r: RetentionRow }) {
  const { t } = useTranslation();
  const change = useSettingsChange();
  const [months, setMonths] = useState<number | ''>(r.monthsInForce ?? '');
  return (
    <form
      className="inline-months"
      onSubmit={(e) => {
        e.preventDefault();
        if (months !== '') change.mutate(() => privacy.setRetention(r.record, months, r.version));
      }}
    >
      <input type="number" min={r.minMonths ?? 1} max={1200} value={months} aria-label={`${t('privacy.months')}: ${r.labelEn}`} onChange={(e) => setMonths(e.target.value === '' ? '' : Number(e.target.value))} />
      <button type="submit" className="btn sm" disabled={change.isPending || months === r.monthsInForce}>
        {t('seq.set')}
      </button>
      {change.isError ? <span className="form-error" role="alert">{errorText(change.error)}</span> : null}
    </form>
  );
}

function RetentionCard({ s, canEdit }: { s: Settings; canEdit: boolean }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language === 'ml' ? 'ml' : 'en';
  return (
    <section className="card">
      <div className="section-h">
        <h2>{t('privacy.retention')}</h2>
      </div>
      <p className="note">{t('privacy.retentionHint')}</p>
      <div className="tbl-wrap">
        <div className="tbl-scroll">
          <table>
            <thead>
              <tr>
                <th>{t('privacy.record')}</th>
                <th>{t('privacy.period')}</th>
                <th>{t('privacy.basis')}</th>
                {canEdit ? <th>{t('privacy.months')}</th> : null}
              </tr>
            </thead>
            <tbody>
              {s.retention.map((r) => (
                <tr key={r.record}>
                  <td className={lang === 'ml' ? 'ml' : undefined}>{lang === 'ml' ? r.labelMl : r.labelEn}</td>
                  <td>
                    {retentionPeriodText({ months: r.monthsInForce, from: r.from }, lang)}
                    {r.custom ? (
                      <>
                        {' '}
                        <Badge tone="info">{t('privacy.custom')}</Badge>
                      </>
                    ) : null}
                  </td>
                  <td className={`note${lang === 'ml' ? ' ml' : ''}`}>{lang === 'ml' ? r.basisMl : r.basisEn}</td>
                  {canEdit ? (
                    <td>
                      {r.editable ? (
                        <>
                          <RetentionMonths r={r} />
                          <span className="note">{t('privacy.min', { n: r.minMonths ?? 1 })}</span>
                        </>
                      ) : (
                        <Badge tone="neutral">{t('privacy.fixed')}</Badge>
                      )}
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

/** Settings › Privacy (pilot slice 5): the mahallu's notice, grievance officer and retention schedule. */
export function PrivacySettings() {
  const { t } = useTranslation();
  const me = useMe();
  const settings = usePrivacySettings();
  const s = settings.data;
  return (
    <>
      <PageHeader icon="shield" title={t('privacy.title')} desc={t('privacy.desc')} />
      {settings.isError ? <p className="form-error">{t(loadErrorKey(settings.error))}</p> : null}
      {!s ? (
        settings.isError ? null : <p className="note">{t('privacy.loading')}</p>
      ) : (
        <div className="privacy-grid">
          <OfficerCard key={`o${s.officer?.version ?? 0}`} s={s} />
          <NoticeCard s={s} />
          <RetentionCard s={s} canEdit={Boolean(me.data?.role)} />
        </div>
      )}
    </>
  );
}
